import { CheckCircle, XCircle } from "lucide-react";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
	percent: number;
	status?: "success" | "exception" | "active" | "normal";
	type?: "line" | "circle" | "dashboard";
	showInfo?: boolean;
	strokeColor?: string | { from: string; to: string };
	trailColor?: string;
	strokeWidth?: number;
	size?: "small" | "default";
	format?: (percent: number) => React.ReactNode;
	steps?: number;
}

const Progress = forwardRef<HTMLDivElement, ProgressProps>(
	(
		{
			className,
			percent,
			status,
			type = "line",
			showInfo = true,
			strokeColor,
			trailColor,
			strokeWidth,
			size = "default",
			format,
			steps,
			...props
		},
		ref,
	) => {
		const clampedPercent = Math.min(100, Math.max(0, percent));

		// 自动判断状态
		const effectiveStatus = status || (clampedPercent >= 100 ? "success" : "normal");

		const statusColors = {
			normal: "bg-primary-500",
			active: "bg-primary-500",
			success: "bg-success-500",
			exception: "bg-error-500",
		};

		const statusTextColors = {
			normal: "text-gray-700 dark:text-gray-200",
			active: "text-gray-700 dark:text-gray-200",
			success: "text-success-500",
			exception: "text-error-500",
		};

		const getStrokeColor = () => {
			if (strokeColor) {
				if (typeof strokeColor === "string") {
					return strokeColor;
				}
				return `linear-gradient(to right, ${strokeColor.from}, ${strokeColor.to})`;
			}
			return undefined;
		};

		const renderInfo = () => {
			if (!showInfo) return null;

			if (format) {
				return format(clampedPercent);
			}

			if (effectiveStatus === "success") {
				return <CheckCircle className="h-4 w-4 text-success-500" />;
			}

			if (effectiveStatus === "exception") {
				return <XCircle className="h-4 w-4 text-error-500" />;
			}

			return (
				<span className={cn("text-sm", statusTextColors[effectiveStatus])}>{clampedPercent}%</span>
			);
		};

		// Line Progress
		if (type === "line") {
			const height = strokeWidth || (size === "small" ? 6 : 8);

			// Steps mode
			if (steps) {
				const filledSteps = Math.round((clampedPercent / 100) * steps);

				return (
					<div ref={ref} className={cn("flex items-center gap-2", className)} {...props}>
						<div className="flex flex-1 gap-0.5">
							{Array.from({ length: steps }).map((_, i) => (
								<div
									key={i}
									className={cn(
										"flex-1 rounded-sm transition-colors",
										i < filledSteps
											? statusColors[effectiveStatus]
											: "bg-gray-200 dark:bg-gray-700",
									)}
									style={{
										height,
										backgroundColor:
											i < filledSteps && strokeColor
												? typeof strokeColor === "string"
													? strokeColor
													: undefined
												: undefined,
									}}
								/>
							))}
						</div>
						{renderInfo()}
					</div>
				);
			}

			return (
				<div ref={ref} className={cn("flex items-center gap-2", className)} {...props}>
					<div
						className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
						style={{
							height,
							backgroundColor: trailColor,
						}}
					>
						<div
							className={cn(
								"h-full rounded-full transition-all duration-300",
								!strokeColor && statusColors[effectiveStatus],
								effectiveStatus === "active" &&
									"relative overflow-hidden after:absolute after:inset-0 after:bg-gradient-to-r after:from-transparent after:via-white/30 after:to-transparent after:animate-[shimmer_2s_infinite]",
							)}
							style={{
								width: `${clampedPercent}%`,
								background: getStrokeColor(),
							}}
						/>
					</div>
					{renderInfo()}
				</div>
			);
		}

		// Circle Progress
		if (type === "circle" || type === "dashboard") {
			const circleSize = size === "small" ? 80 : 120;
			const lineWidth = strokeWidth || (size === "small" ? 4 : 6);
			const radius = (circleSize - lineWidth) / 2;
			const circumference = 2 * Math.PI * radius;
			const dashOffset =
				type === "dashboard"
					? circumference * (1 - (clampedPercent / 100) * 0.75)
					: circumference * (1 - clampedPercent / 100);

			const strokeColorValue =
				typeof strokeColor === "string"
					? strokeColor
					: effectiveStatus === "success"
						? "#52c41a"
						: effectiveStatus === "exception"
							? "#f5222d"
							: "#1677ff";

			return (
				<div
					ref={ref}
					className={cn("relative inline-flex items-center justify-center", className)}
					style={{ width: circleSize, height: circleSize }}
					{...props}
				>
					<svg
						viewBox={`0 0 ${circleSize} ${circleSize}`}
						className="transform -rotate-90"
						style={type === "dashboard" ? { transform: "rotate(-135deg)" } : undefined}
					>
						{/* Trail */}
						<circle
							cx={circleSize / 2}
							cy={circleSize / 2}
							r={radius}
							fill="none"
							stroke={trailColor || "currentColor"}
							strokeWidth={lineWidth}
							className="text-gray-200 dark:text-gray-700"
							strokeDasharray={
								type === "dashboard" ? `${circumference * 0.75} ${circumference}` : undefined
							}
						/>
						{/* Progress */}
						<circle
							cx={circleSize / 2}
							cy={circleSize / 2}
							r={radius}
							fill="none"
							stroke={strokeColorValue}
							strokeWidth={lineWidth}
							strokeLinecap="round"
							strokeDasharray={circumference}
							strokeDashoffset={dashOffset}
							className="transition-all duration-300"
						/>
					</svg>
					{showInfo && (
						<div className="absolute inset-0 flex items-center justify-center">
							{format ? (
								format(clampedPercent)
							) : effectiveStatus === "success" ? (
								<CheckCircle className="h-8 w-8 text-success-500" />
							) : effectiveStatus === "exception" ? (
								<XCircle className="h-8 w-8 text-error-500" />
							) : (
								<span
									className={cn(
										"font-medium",
										size === "small" ? "text-lg" : "text-2xl",
										statusTextColors[effectiveStatus],
									)}
								>
									{clampedPercent}%
								</span>
							)}
						</div>
					)}
				</div>
			);
		}

		return null;
	},
);

Progress.displayName = "Progress";

export { Progress };
